// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { nativeScrollViewProps } from 'components/common/scroll/TreactNativeScrollStyles';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import Ellipsis from 'components/treactui-template/atoms/Ellipsis';
import useAutoScrollToSelected from './hooks/useAutoScrollToSelected';
import { useTranslation } from 'react-i18next';
import { ImageType } from 'model/Image';
import ImageTableEntry from './ImageTableEntry';
import { useAppSelector } from 'store/hooks';
import sortBy from 'lodash/sortBy';
import styled from 'styled-components';
import { SSimContext } from 'components/project/label/similarity/SSimContext';
import { ImageListOrderContext } from './ImageListOrderContext';
import { Paging, Table } from '@tls/treact-ui';
import TestableLoader from 'components/treactui-template/atoms/TestableLoader';

export type Props = {
  data: ImageType[];
  loading: boolean;
  isQC?: boolean;
};

const ENTRIES_PER_PAGE = 50;

export default function ImageTable({ data, loading, isQC }: Readonly<Props>) {
  const { t } = useTranslation();

  const { selectedMd5 } = useAppSelector(s => s.image);
  const { result } = useContext(SSimContext);
  const { currentOrder } = useContext(ImageListOrderContext);

  const sortArray = useCallback((array: ImageType[], sortProp: string) => sortBy(array, sortProp), []);

  const onSortByName = useCallback((array: ImageType[]) => sortArray(array, 'imageName'), [sortArray]);
  const onSortByLabel = useCallback((array: ImageType[]) => sortArray(array, 'hasMask'), [sortArray]);
  const onSortByLabelOrder = useCallback((array: ImageType[]) => sortArray(array, 'labelOrder'), [sortArray]);

  const dataWithLabelOrder = useMemo<ImageType[]>(() => {
    const maxOrder = result?.length > 0 ? result.length + 1 : undefined;
    return data.map(d => ({ ...d, labelOrder: result.find(r => r.md5 === d.imageMd5)?.labelOrder ?? maxOrder }));
  }, [result, data]);

  const [page, setPage] = useState(1);

  // Hook handling initial auto page/scroll to selected row. registerRowRef returns a ref callback for rows.
  const { registerRowRef } = useAutoScrollToSelected({
    selectedMd5,
    currentOrder,
    page,
    setPage,
    pageSize: ENTRIES_PER_PAGE,
  });

  const pagingIsActive = dataWithLabelOrder.length > ENTRIES_PER_PAGE - 1;
  return (
    <TableContainer $pagingIsActive={`${pagingIsActive}`} $loading={`${loading}`}>
      <Table list={dataWithLabelOrder} data-testid='label-images-table'>
        {tableList => {
          if (currentOrder) currentOrder.current = tableList.map(t => t.imageMd5 ?? '');
          return (
            <>
              <Table.Header>
                <Table.HeaderRow>
                  <Table.HeaderSortCell
                    noIndent
                    width={isQC ? '30%' : '22%'}
                    alignment='LEFT'
                    sortKey='label'
                    sortFn={onSortByLabel}
                    data-testid='label-images-table-label-sort-button'
                  >
                    <Ellipsis>{t('project.label.images.table.label')}</Ellipsis>
                  </Table.HeaderSortCell>
                  <Table.HeaderSortCell
                    width={1}
                    alignment='LEFT'
                    sortKey='name'
                    sortFn={onSortByName}
                    data-testid='label-images-table-name-sort-button'
                  >
                    {t('project.label.images.table.name')}
                  </Table.HeaderSortCell>
                  <Table.HeaderSortCell
                    width='25%'
                    alignment='LEFT'
                    sortKey='labelOrder'
                    sortFn={onSortByLabelOrder}
                    data-testid='label-images-table-order-sort-button'
                  >
                    <Ellipsis>{t('project.label.images.table.order')}</Ellipsis>
                  </Table.HeaderSortCell>
                </Table.HeaderRow>
              </Table.Header>
              <Table.ScrollingBody scrollViewProps={nativeScrollViewProps}>
                {tableList
                  .slice((page - 1) * ENTRIES_PER_PAGE, page * ENTRIES_PER_PAGE)
                  .filter(t => t.imageName)
                  .map((entry, idx) => (
                    <ImageTableEntry
                      key={`${entry.imageName}:${entry.imageMd5}`}
                      entry={entry}
                      index={idx}
                      selected={entry.imageMd5 === selectedMd5}
                      ref={registerRowRef(entry.imageMd5)}
                    />
                  ))}
              </Table.ScrollingBody>
            </>
          );
        }}
      </Table>
      {loading && <TestableLoader label='' data-testid='label-images-table-loader' />}
      {pagingIsActive && (
        <PagingContainer>
          <Paging
            id='paging'
            currentPage={page}
            itemCount={dataWithLabelOrder.length}
            onSelect={setPage}
            popupLabel={t('project.label.images.pagination.goToPage')}
            pageSize={ENTRIES_PER_PAGE}
            data-testid='label-images-pagination-control'
          />
        </PagingContainer>
      )}
    </TableContainer>
  );
}

const PagingContainer = styled.div`
  height: 3.5rem;
  margin: 0.5rem 0.5rem 0 0;

  #paging {
    justify-content: center;
  }
`;

const TableContainer = styled.div<{ $pagingIsActive: string; $loading: string }>`
  position: relative;
  height: calc(
    100% ${({ $pagingIsActive }) => ($pagingIsActive === 'true' ? '- 3.5rem' : '')}
      ${({ $loading }) => ($loading === 'true' ? '- 2.5rem' : '')}
  );
`;
